import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  X,
  Pencil,
  Trash2,
  FileText,
  CreditCard,
  RotateCcw,
  Target,
  CheckCircle,
  Loader2,
  Banknote,
  ChevronLeft,
  Store,
  Tag,
  Percent,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useProducts } from "../context/ProductsContext";
import {
  storePosBooking,
  SunbedItem,
  PosStoreResponse,
  applyPosCoupon,
} from "../services/posApi";
import { useAuth } from "../auth/AuthContext";
import { useOffline } from "../context/OfflineContext";
import {
  createQueuedPosSaleInput,
} from "../services/offlinePosSync";
import {
  enqueueOfflinePosSale,
  isCashOnlyTender,
} from "../lib/offlinePosQueue";
import { isBrowserOffline, shouldUseCachedData } from "../lib/networkStatus";
import PaymentCompletedModal from "./PaymentCompletedModal";
import GlobalLoader from "./ui/GlobalLoader";
import { cn } from "../lib/utils";
import { useToast } from "../hooks/useToast";
import { Service } from "../types/calendar";
import { isShellAppAvailable, shellPay } from "../lib/shellApp";
import { patchPosDisplayState } from "../lib/posDisplaySync";

type SidebarMode = "quick" | "paynow";

interface PosPaymentStepProps {
  selectedService: Service;
  selectedCustomer: string;
  appointmentTime: string;
  tip: number;
  setTip: React.Dispatch<React.SetStateAction<number>>;
  goToFinalPayment: () => void;
  onClose: () => void;
  mode: SidebarMode;
  locationId?: number | string;
  userId?: number | string;
  onPaymentComplete?: (bookingId: number | string) => void;
  onCustomerDisplayUpdate?: (update: {
    status: "awaiting" | "card" | "tendered" | "saving" | "complete";
    tenderLabel?: string | null;
    amountDue?: number;
    totalTendered?: number;
    changeDue?: number;
    amountPaid?: number;
  }) => void;
  // Services data (for appointments/bookings)
  services?: Array<{
    id: string | number;
    quantity?: number;
    price: number;
    taxPercent?: number;
  }>;
  // Sunbeds data (for appointments/bookings)
  sunbeds?: SunbedItem[];
  memberships?: Array<{ plan_id: number; price?: number }>;
  // Employee assignment
  employees?: (number | string | null)[];
  posDate?: string; // Optional custom date in Y-m-d format
  isEmbedded?: boolean;
  onBack?: () => void;
  initialAmountPaid?: number;
  initialPendingAmount?: number;
  paymentBookingId?: number | string;
  consultation_form_ids?: number[];
  /** When true, backend will set in_store_usage = 1 for product items (e.g. Store Usage button in POS). */
  inStoreUsage?: boolean;
  /** When true, shows the Store Usage button (e.g. POS product cart). Hidden for dashboard/services/sunbed flows. */
  showStoreUsageButton?: boolean;
  /** Products added from the booking sidebar (bypasses POS products context) */
  cartProducts?: Array<{
    id: string | number;
    quantity: number;
    price: number;
    taxPercent?: number;
  }>;
  /** Deals from POS Quick Sale cart */
  deals?: Array<{
    id: number | string;
    quantity?: number;
    price: number;
  }>;
}

const PosPaymentStep: React.FC<PosPaymentStepProps> = ({
  selectedService,
  selectedCustomer,
  appointmentTime,
  tip,
  setTip,
  goToFinalPayment,
  onClose,
  mode,
  locationId,
  userId,
  onPaymentComplete,
  onCustomerDisplayUpdate,
  services,
  sunbeds,
  memberships,
  employees,
  posDate,
  isEmbedded = false,
  onBack,
  initialAmountPaid = 0,
  initialPendingAmount,
  paymentBookingId,
  consultation_form_ids,
  inStoreUsage: inStoreUsageProp = false,
  showStoreUsageButton = false,
  cartProducts,
  deals,
}) => {
  const toast = useToast();
  const [isStoreUsage, setIsStoreUsage] = useState(inStoreUsageProp);
  const [selectedTip, setSelectedTip] = useState<number>(5);
  const [isPaid, setIsPaid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [apiResponse, setApiResponse] = useState<any>(null);
  const [completedBookingId, setCompletedBookingId] = useState<
    number | string | null
  >(null);
  const [hasError, setHasError] = useState(false);
  const [isOfflineQueuedSale, setIsOfflineQueuedSale] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState<{
    id: number;
    code: string;
    amount: number;
  } | null>(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [cartDiscountType, setCartDiscountType] = useState<"percent" | "fixed">(
    "percent",
  );
  const [cartDiscountInput, setCartDiscountInput] = useState("");
  const [cartDiscountPercent, setCartDiscountPercent] = useState(0);
  const [cartDiscountAmount, setCartDiscountAmount] = useState(0);
  const { products } = useProducts();
  const { user } = useAuth();
  const { isOffline } = useOffline();
  const currencySymbol =
    user?.company?.currency_symbol ||
    user?.company?.currency?.currency_symbol ||
    "";
  const currencyCode = user?.company?.currency?.code?.toLowerCase() || "gbp";

  const paymentProcessedRef = useRef(false);
  const apiCallAttemptedRef = useRef(false); // Track if API call has been attempted
  const displayPhaseRef = useRef<string>("awaiting"); // Track customer display phase to prevent mirror-effect overwrite

  const dispatchDisplayUpdate = useCallback(
    (update: Parameters<NonNullable<typeof onCustomerDisplayUpdate>>[0]) => {
      displayPhaseRef.current = update.status;
      // Keep the 2nd screen on processing until POS holds Thank you for a moment
      const liveStatus = update.status === "complete" ? "saving" : update.status;
      patchPosDisplayState({
        paymentStatus: liveStatus,
        tenderLabel: update.tenderLabel ?? null,
        amountDue: update.amountDue ?? null,
        totalTendered: update.totalTendered ?? null,
        changeDue: update.changeDue ?? null,
        amountPaid: update.amountPaid ?? null,
      });
      onCustomerDisplayUpdate?.(update);
    },
    [onCustomerDisplayUpdate],
  );

  // Check if XE Connect is active
  const isPayWithXEConnect = user?.company?.pay_with_xeconnect === "active";
  const isTabletApp = isShellAppAvailable();

  const isMethodDisabledWhileOffline = (methodId: string): boolean => {
    if (!isOffline) return false;
    return methodId !== "cash";
  };

  const getPaymentMethodLabelOffline = (methodId: string, label: string): string => {
    if (!isOffline) return label;
    if (methodId === "card" && isPayWithXEConnect) {
      return "Card (offline)";
    }
    if (methodId !== "cash") {
      return `${label} (offline)`;
    }
    return label;
  };

  // XE Connect payment functions
  const checkXEServerUp = async (): Promise<boolean> => {
    try {
      const response = await fetch(
        "http://localhost:5050/api/Xepos/IsServerUp",
        {
          method: "GET",
        },
      );
      return response.ok;
    } catch (error) {
      console.error("XE Connect server check failed:", error);
      return false;
    }
  };

  const processXEPayment = async (
    totalAmount: number,
  ): Promise<{ approved: boolean; transactionId?: string; uuid?: string }> => {
    // Generate random text for OrderNo
    const randomText =
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);
    // Format amount: multiply by 100 and convert to string (as per original logic)
    const formattedAmount = Math.round(totalAmount * 100).toString();

    try {
      // Check if server is up
      const isServerUp = await checkXEServerUp();
      if (!isServerUp) {
        toast.error("Please first run the Xe-Connect server!");
        return { approved: false };
      }

      // Process payment - send as Form Data
      const formData = new URLSearchParams();
      formData.append("Amount", formattedAmount);
      formData.append("OrderNo", randomText);

      const response = await fetch(
        "http://localhost:5050/api/Xepos/Evo/Websale",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: formData.toString(),
        },
      );

      const data = await response.json();

      if (data.StatusMessage === "Approved") {
        toast.success("Card payment is done successfully!");
        return {
          approved: true,
          transactionId: data.TransactionId,
          uuid: randomText,
        };
      } else {
        toast.error("Card payment is cancelled/declined!");
        return { approved: false };
      }
    } catch (error) {
      console.error("XE Connect payment error:", error);
      toast.error("Something went wrong, please try again later!");
      return { approved: false };
    }
  };

  // Payment calculations
  // Calculate subtotal from products (POS), services (appointments), and sunbeds
  // Calculate subtotal from products (POS), services (appointments), and sunbeds
  // Calculate subtotal from products (POS), services (appointments), and sunbeds

  // Normalize to a common shape before reducing, so TypeScript is happy.
  // When cartProducts is provided (booking sidebar) use those; otherwise use POS context products.
  interface NormalizedProduct {
    price: number;
    discountedPrice?: number;
    taxes: { percent: number }[];
    quantity: number;
  }

  const normalizedProducts: NormalizedProduct[] =
    cartProducts && cartProducts.length > 0
      ? cartProducts.map((cp) => ({
          price: cp.price,
          discountedPrice: undefined,
          taxes: cp.taxPercent ? [{ percent: cp.taxPercent }] : [],
          quantity: cp.quantity,
        }))
      : products.map((p) => ({
          price: p.price ?? 0,
          discountedPrice: p.discountedPrice,
          taxes: p.taxes?.map((t) => ({ percent: t.percent })) ?? [],
          quantity: p.quantity ?? 1,
        }));

  const { productsPrice, productsTax } = normalizedProducts.reduce(
    (acc, p) => {
      const totalTaxPercent = p.taxes.reduce((sum, t) => sum + t.percent, 0);

      let price = p.price;
      if (p.discountedPrice !== undefined && p.discountedPrice !== null && p.discountedPrice < p.price) {
        price = p.discountedPrice;
      }

      const lineTotal = price * p.quantity;
      const lineTax = (lineTotal * totalTaxPercent) / 100;

      return {
        productsPrice: acc.productsPrice + lineTotal,
        productsTax: acc.productsTax + lineTax,
      };
    },
    { productsPrice: 0, productsTax: 0 },
  );

  const { servicesPrice, servicesTax } = (services || []).reduce(
    (acc, s) => {
      const price = s.price || 0;
      const quantity = s.quantity || 1;
      const taxPercent = s.taxPercent || 0;

      const lineTotal = price * quantity;
      const lineTax = (lineTotal * taxPercent) / 100;

      return {
        servicesPrice: acc.servicesPrice + lineTotal,
        servicesTax: acc.servicesTax + lineTax,
      };
    },
    { servicesPrice: 0, servicesTax: 0 },
  );

  // Calculate sunbeds subtotal
  const sunbedsSubtotal =
    sunbeds?.reduce((sum, sunbed) => {
      // Find selected pricing
      const selectedPricing = sunbed.pricing.find(
        (p: { id: number | string }) => p.id === sunbed.selectedPricingId,
      );
      if (!selectedPricing) return sum;

      // Calculate amount based on pricing type
      if (selectedPricing.type === "fixed") {
        const price =
          typeof selectedPricing.price === "string"
            ? parseFloat(selectedPricing.price)
            : selectedPricing.price;
        return sum + price;
      } else {
        // per_minute
        const minutes = sunbed.selectedMinutes || 0;
        const pricePerMinute =
          typeof selectedPricing.price === "string"
            ? parseFloat(selectedPricing.price)
            : selectedPricing.price;
        return sum + minutes * pricePerMinute;
      }
    }, 0) ?? 0;

  const membershipsSubtotal =
    memberships?.reduce((sum, membership) => {
      const price = Number(membership.price ?? 0);
      return sum + (Number.isFinite(price) ? price : 0);
    }, 0) ?? 0;

  const subtotal = productsPrice + servicesPrice + sunbedsSubtotal + membershipsSubtotal;
  const tax = productsTax + servicesTax;
  const total = subtotal + tax;
  const cartDiscount = Math.min(
    Math.max(0, cartDiscountAmount),
    Math.max(0, subtotal),
  );
  const totalAfterCartDiscount = Math.max(0, total - cartDiscount);
  const couponDiscount = appliedCoupon?.amount ?? 0;
  const totalAfterCoupon = Math.max(0, totalAfterCartDiscount - couponDiscount);

  // Tendered payments state
  interface TenderedPayment {
    id: string;
    type: "cash" | "cheque" | "card" | "card_reader";
    amount: number;
    transactionId?: string;
    uuid?: string;
  }

  const [tenderedPayments, setTenderedPayments] = useState<TenderedPayment[]>(
    [],
  );

  // Calculate totals
  const totalTendered = tenderedPayments.reduce((sum, p) => sum + p.amount, 0);

  // Try to use initialPendingAmount if valid, otherwise fallback to total (after coupon)
  // Be careful if total is 0 but we have a pending amount (unlikely but possible logic)
  const actualTotalToPay =
    initialPendingAmount !== undefined && paymentBookingId
      ? initialPendingAmount
      : totalAfterCoupon;

  const toPay = Math.max(0, actualTotalToPay - totalTendered);
  const changeDue =
    totalTendered > actualTotalToPay ? totalTendered - actualTotalToPay : 0;

  // Calculator state
  const [paymentAmount, setPaymentAmount] = useState(toPay.toFixed(2));
  const [hasDecimal, setHasDecimal] = useState(false);
  const [decimalDigits, setDecimalDigits] = useState(0);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    "cash" | "cheque" | "card" | "card_reader" | "store_usage" | null
  >(null);
  const [isUserTyping, setIsUserTyping] = useState(false);

  useEffect(() => {
    if (!isOffline) return;
    if (
      selectedPaymentMethod &&
      selectedPaymentMethod !== "cash" &&
      selectedPaymentMethod !== "store_usage"
    ) {
      setSelectedPaymentMethod("cash");
    }
  }, [isOffline, selectedPaymentMethod]);

  // Reset payment processed flag when products change (new transaction)
  React.useEffect(() => {
    paymentProcessedRef.current = false;
    apiCallAttemptedRef.current = false;
    displayPhaseRef.current = "awaiting";
  }, [products.length, services?.length]);

  // Update payment amount when toPay changes (only if user is not typing)
  React.useEffect(() => {
    if (!isUserTyping) {
      const newAmount = toPay.toFixed(2);
      setPaymentAmount(newAmount);
      setHasDecimal(false);
      setDecimalDigits(0);
    }
  }, [toPay, isUserTyping]);

  // Clear coupon/discount when cart total changes (items added/removed/repriced)
  React.useEffect(() => {
    if (!appliedCoupon) return;
    setAppliedCoupon(null);
    setCouponError(null);
  }, [total]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (cartDiscountPercent <= 0 && cartDiscountAmount <= 0) return;
    setCartDiscountPercent(0);
    setCartDiscountAmount(0);
    setCartDiscountInput("");
  }, [subtotal]); // eslint-disable-line react-hooks/exhaustive-deps

  const canApplyCoupon =
    !paymentBookingId && !isOffline && total > 0 && !isSubmitting;

  const canApplyCartDiscount =
    !paymentBookingId && total > 0 && !isSubmitting;

  const handleApplyCoupon = async () => {
    const code = couponCode.trim();
    if (!code) {
      setCouponError("Enter a coupon code");
      return;
    }
    if (!canApplyCoupon) {
      setCouponError(
        isOffline
          ? "Coupons require an internet connection"
          : "Coupon cannot be applied to this payment",
      );
      return;
    }

    setCouponLoading(true);
    setCouponError(null);
    try {
      const result = await applyPosCoupon({
        coupon: code,
        cart_amount: Number(totalAfterCartDiscount.toFixed(2)),
        cart_discount: cartDiscountPercent,
      });
      const discount = Math.min(
        Math.max(0, Number(result.amount) || 0),
        totalAfterCartDiscount,
      );
      if (discount <= 0) {
        setCouponError("Coupon has no discount for this cart");
        setAppliedCoupon(null);
        return;
      }
      setAppliedCoupon({
        id: result.coupon_data.id,
        code: result.coupon_data.code || code,
        amount: discount,
      });
      setCouponCode(result.coupon_data.code || code);
      toast.success(result.message || "Coupon applied");
      setCouponModalOpen(false);
      setCouponError(null);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string; message?: string } } })
          ?.response?.data?.error ||
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ||
        (err instanceof Error ? err.message : "Failed to apply coupon");
      setCouponError(msg);
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    setCouponError(null);
    setCouponModalOpen(false);
  };

  const openCouponModal = () => {
    setCouponError(null);
    if (appliedCoupon) {
      setCouponCode(appliedCoupon.code);
    }
    setCouponModalOpen(true);
  };

  const closeCouponModal = () => {
    if (couponLoading) return;
    setCouponModalOpen(false);
    setCouponError(null);
    if (!appliedCoupon) {
      setCouponCode("");
    }
  };

  const openDiscountModal = () => {
    if (cartDiscountAmount > 0) {
      setCartDiscountInput(
        cartDiscountType === "percent"
          ? String(cartDiscountPercent)
          : String(cartDiscountAmount),
      );
    } else {
      setCartDiscountInput("");
    }
    setDiscountModalOpen(true);
  };

  const closeDiscountModal = () => {
    setDiscountModalOpen(false);
  };

  const handleApplyCartDiscount = () => {
    const raw = parseFloat(cartDiscountInput);
    if (!Number.isFinite(raw) || raw <= 0) {
      toast.error("Enter a valid discount");
      return;
    }
    if (subtotal <= 0) {
      toast.error("Cart is empty");
      return;
    }

    let percent = 0;
    let amount = 0;
    if (cartDiscountType === "percent") {
      if (raw > 100) {
        toast.error("Discount percentage cannot exceed 100%");
        return;
      }
      percent = raw;
      amount = (raw / 100) * subtotal;
    } else {
      if (raw > subtotal) {
        toast.error("Discount cannot exceed cart subtotal");
        return;
      }
      amount = raw;
      percent = subtotal > 0 ? (raw / subtotal) * 100 : 0;
    }

    setCartDiscountPercent(Number(percent.toFixed(4)));
    setCartDiscountAmount(Number(amount.toFixed(2)));
    // Coupon was based on previous total — clear so user re-applies if needed
    if (appliedCoupon) {
      setAppliedCoupon(null);
      setCouponCode("");
    }
    setDiscountModalOpen(false);
    toast.success("Discount applied");
  };

  const handleRemoveCartDiscount = () => {
    setCartDiscountPercent(0);
    setCartDiscountAmount(0);
    setCartDiscountInput("");
    setDiscountModalOpen(false);
  };

  // Calculator keypad handler
  const handleKey = (key: string) => {
    setIsUserTyping(true);
    setPaymentAmount((prev) => {
      let [intPart, decPart] = prev.split(".");
      decPart = decPart || "00";

      // If display still shows the default "to pay" amount, treat next keypress as starting fresh (replace, not append)
      const isShowingDefault = Math.abs(parseFloat(prev) - toPay) < 0.005;

      // DELETE key
      if (key === "del") {
        if (hasDecimal && decimalDigits > 0) {
          decPart =
            decPart.slice(0, decimalDigits - 1) +
            "0".repeat(2 - (decimalDigits - 1));
          setDecimalDigits(decimalDigits - 1);
          if (decimalDigits - 1 === 0) setHasDecimal(false);
          return `${intPart}.${decPart}`;
        } else if (intPart.length > 1) {
          intPart = intPart.slice(0, -1);
          return `${intPart}.00`;
        } else {
          return "0.00";
        }
      }

      // DECIMAL key
      if (key === ".") {
        if (hasDecimal) return prev;
        setHasDecimal(true);
        setDecimalDigits(0);
        return `${intPart}.`;
      }

      // NUMBER keys
      if (/^\d$/.test(key)) {
        if (!hasDecimal) {
          if (isShowingDefault) {
            // Start from the pressed digit (e.g. 8 -> "8.00")
            intPart = key;
          } else if (intPart === "0") {
            intPart = key;
          } else {
            intPart += key;
          }
          return `${intPart}.00`;
        } else {
          // Append to decimal part (max 2 digits)
          if (decimalDigits >= 2) return prev;
          const newDec = (decPart.slice(0, decimalDigits) + key).padEnd(2, "0");
          setDecimalDigits(decimalDigits + 1);
          return `${intPart}.${newDec}`;
        }
      }

      // Handle "00" key
      if (key === "00") {
        if (!hasDecimal) {
          if (isShowingDefault || intPart === "0") {
            return "0.00";
          } else {
            intPart += "00";
            return `${intPart}.00`;
          }
        } else {
          // Can't add 00 to decimal part
          return prev;
        }
      }

      return prev;
    });
  };

  // Denomination shortcuts (£1, £5, £10…) — replace display amount, do not add to current
  const handleDenomination = (amount: number) => {
    setIsUserTyping(true);
    setPaymentAmount(amount.toFixed(2));
    setHasDecimal(false);
    setDecimalDigits(0);
  };

  // Handle remove tendered payment
  const handleRemovePayment = (id: string) => {
    setTenderedPayments(tenderedPayments.filter((p) => p.id !== id));
  };

  // Handle payment method selection - automatically add payment
  const handlePaymentMethodClick = async (
    method: "cash" | "cheque" | "card" | "card_reader",
    options?: { skipSelect?: boolean; amount?: number },
  ) => {
    if (!userId) {
      toast.error("Please select a customer first.");
      return;
    }

    if (isMethodDisabledWhileOffline(method)) {
      toast.error(
        method === "card" && isPayWithXEConnect
          ? "Card / XE Connect is unavailable while offline. Use cash."
          : "Only cash payments are available while offline.",
      );
      return;
    }

    if (!options?.skipSelect) {
      setSelectedPaymentMethod(method);
    }

    // ===============================
    // CARD READER (Tablet Only)
    // ===============================
    if (method === "card_reader") {
      if (!isTabletApp) {
        toast.error("Card Reader is only available on tablet");
        return;
      }

      let amountToAdd = parseFloat(paymentAmount);

      // If user didn't type, use remaining balance
      if (!isUserTyping) {
        amountToAdd = toPay;
      }

      if (amountToAdd <= 0) {
        toast.error("Please enter a valid payment amount");
        return;
      }

      if (amountToAdd > toPay) {
        toast.error(
          `Payment amount cannot exceed ${currencySymbol}${toPay.toFixed(2)}`,
        );
        return;
      }

      setIsSubmitting(true);
      dispatchDisplayUpdate({
        status: "card",
        tenderLabel: "Card Reader",
        amountDue: amountToAdd,
      });

      try {
        const result = await shellPay({
          amount: Math.round(amountToAdd * 100), // cents
          currency: currencyCode,
          description: "POS Card Reader Payment",
          metadata: {
            user_id: userId,
            location_id: locationId,
          },
        });

        if (!result.ok || !result.data?.success) {
          throw new Error("Card reader payment failed");
        }

        const newPayment: TenderedPayment = {
          id: Date.now().toString(),
          type: "card_reader",
          amount: amountToAdd,
          transactionId: result.data.payment_intent_id,
        };

        setTenderedPayments([...tenderedPayments, newPayment]);
        setIsUserTyping(false);
      } catch (error) {
        console.error("Card Reader payment error:", error);
        toast.error("Card reader payment failed");
        dispatchDisplayUpdate({
          status: "awaiting",
          tenderLabel: null,
          amountDue: toPay,
        });
      } finally {
        setIsSubmitting(false);
      }

      return; // ⛔ VERY IMPORTANT — prevents fallthrough
    }

    // Get the current payment amount (or use explicit amount e.g. from Store Usage)
    let amountToAdd =
      options?.amount != null
        ? options.amount
        : parseFloat(paymentAmount);

    // If user hasn't typed anything and no explicit amount, use remaining balance
    if (options?.amount == null && !isUserTyping) {
      amountToAdd = toPay;
      setPaymentAmount(toPay.toFixed(2));
      setHasDecimal(false);
      setDecimalDigits(0);
    }

    // When explicit amount is passed (e.g. Store Usage), sync display to it
    if (options?.amount != null) {
      setPaymentAmount(options.amount.toFixed(2));
      setHasDecimal(false);
      setDecimalDigits(0);
      setIsUserTyping(false);
    }

    // Automatically add payment to tendered payments
    if (amountToAdd > 0) {
      // Cash (incl. quick-money denominations) may exceed To Pay — change is due and payment completes instantly.
      // Card / cheque must not exceed remaining balance.
      if (amountToAdd > toPay && method !== "cash") {
        toast.error(
          `Payment amount cannot exceed remaining balance of ${currencySymbol}${toPay.toFixed(2)}`,
        );
        return;
      }

      // If card payment and XE Connect is enabled, process through XE Connect first
      if (method === "card" && isPayWithXEConnect) {
        if (isOffline) {
          toast.error("Card payments are unavailable while offline.");
          return;
        }
        setIsSubmitting(true);
        dispatchDisplayUpdate({
          status: "card",
          tenderLabel: "Card",
          amountDue: amountToAdd,
        });
        try {
          const xeResult = await processXEPayment(amountToAdd);
          setIsSubmitting(false);

          if (!xeResult.approved) {
            // Payment was declined or failed, don't add to tendered payments
            dispatchDisplayUpdate({
              status: "awaiting",
              tenderLabel: null,
              amountDue: toPay,
            });
            return;
          }

          // Payment approved, add to tendered payments with transaction details
          const newPayment: TenderedPayment = {
            id: Date.now().toString(),
            type: method,
            amount: amountToAdd,
            transactionId: xeResult.transactionId,
            uuid: xeResult.uuid,
          };

          setTenderedPayments([...tenderedPayments, newPayment]);
          setIsUserTyping(false);
          return;
        } catch (error) {
          console.error("XE Connect payment processing error:", error);
          setIsSubmitting(false);
          return;
        }
      }

      // For non-XE Connect payments or non-card payments
      const newPayment: TenderedPayment = {
        id: Date.now().toString(),
        type: method,
        amount: amountToAdd,
      };

      setTenderedPayments([...tenderedPayments, newPayment]);
      setIsUserTyping(false);
      // Payment amount will reset to new toPay via useEffect
      // Payment completion will be handled by useEffect watching toPay
    } else if (amountToAdd <= 0) {
      toast.error("Please enter a valid payment amount");
    }
  };

  // Get payment method label
  const getPaymentMethodLabel = (type: string) => {
    switch (type) {
      case "cash":
        return "Cash";
      case "cheque":
        return "Cheque";
      case "card":
        return "Card";
      case "card_reader":
        return "Card Reader";
      default:
        return type;
    }
  };

  // Handle payment completion (Pay Later or Part Paid)
  const handlePaymentComplete = useCallback(
    async (paymentType: "pay_later" | "part_paid") => {
      if (isSubmitting) {
        return;
      }

      // Prevent duplicate calls - check both flags
      if (paymentProcessedRef.current || apiCallAttemptedRef.current) {
        return;
      }

      // Mark that we're attempting the API call (set before any async operations)
      apiCallAttemptedRef.current = true;
      paymentProcessedRef.current = true;
      setHasError(false);
      setIsPaid(false);
      setIsOfflineQueuedSale(false);

      // Validate required fields
      if (!locationId) {
        // Don't reset flags - API call was attempted, prevent retry
        setIsSubmitting(false);
        toast.error("Location is required. Please select a location.");
        return;
      }

      if (!userId) {
        // Don't reset flags - API call was attempted, prevent retry
        setIsSubmitting(false);
        toast.error("Customer is required. Please select a customer.");
        return;
      }

      setIsSubmitting(true);
      dispatchDisplayUpdate({ status: "saving" });
      const startTime = Date.now();
      const minProcessingTime = 500; // Minimum 0.5 seconds

      try {
        // Prepare service data (for appointments/bookings)
        const serviceIds: (string | number)[] = [];
        const serviceQuantities: number[] = [];
        const servicePrices: number[] = [];
        const serviceTaxPercents: number[] = [];

        if (services && services.length > 0) {
          services.forEach((service) => {
            if (service.id) {
              serviceIds.push(service.id);
              serviceQuantities.push(service.quantity || 1);
              servicePrices.push(service.price || 0);
              serviceTaxPercents.push(service.taxPercent || 0);
            }
          });
        }

        // Prepare product data with tax percentages
        const productIds: (string | number)[] = [];
        const productQuantities: number[] = [];
        const productPrices: number[] = [];
        const productTaxPercents: number[] = [];

        // If cartProducts is provided (booking sidebar), use those instead of POS context
        const productSource = cartProducts && cartProducts.length > 0 ? [] : products;

        productSource.forEach((product) => {
          if (product.id && product.quantity && product.price !== undefined) {
            productIds.push(product.id);
            productQuantities.push(product.quantity);

            let price = product.price;
            if (
              product.discountedPrice !== undefined &&
              product.discountedPrice !== null &&
              product.discountedPrice < product.price
            ) {
              price = product.discountedPrice;
            }
            productPrices.push(price);

            const productTaxPercent =
              product.taxes?.reduce((sum, tax) => sum + tax.percent, 0) || 0;
            productTaxPercents.push(productTaxPercent);
          }
        });

        // Merge booking-sidebar cartProducts (always in_store_usage = 0)
        if (cartProducts && cartProducts.length > 0) {
          cartProducts.forEach((cp) => {
            productIds.push(cp.id);
            productQuantities.push(cp.quantity);
            productPrices.push(cp.price);
            productTaxPercents.push(cp.taxPercent ?? 0);
          });
        }

        // Get date and time in multiple formats
        const now = new Date();
        // Use provided posDate or default to current date
        const finalPosDate = posDate || now.toISOString().split("T")[0];

        // Parse components for d-m-Y format
        let day, month, year;
        if (posDate) {
          const parts = posDate.split("-");
          year = parts[0];
          month = parts[1];
          day = parts[2];
        } else {
          day = String(now.getDate()).padStart(2, "0");
          month = String(now.getMonth() + 1).padStart(2, "0");
          year = now.getFullYear();
        }
        const dateDMY = `${day}-${month}-${year}`;

        // Use appointmentTime prop for time if available, otherwise current time
        const finalPosTime =
          appointmentTime ||
          (() => {
            const hours = now.getHours();
            const minutes = now.getMinutes();
            const ampm = hours >= 12 ? "pm" : "am";
            const displayHours = hours % 12 || 12;
            return `${String(displayHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${ampm}`;
          })();

        // Format payments array
        const payments = tenderedPayments.map((payment) => ({
          type: payment.type === "card_reader" ? "card" : payment.type,
          amount: payment.amount,
        }));

        // Extract transaction_id and uuid from card payments (XE Connect)
        let transactionId: string | null = null;
        let uuid: string | null = null;
        const cardPayment = tenderedPayments.find((p) => p.type === "card");
        if (cardPayment && cardPayment.transactionId && cardPayment.uuid) {
          transactionId = cardPayment.transactionId;
          uuid = cardPayment.uuid;
        }

        // Determine payment type
        const isPayLater =
          paymentType === "pay_later" || tenderedPayments.length === 0;
        const type = isPayLater ? "pay_later" : "part_paid";

        // Determine payment gateway (Multiple if more than one payment method)
        const paymentGateway =
          payments.length > 1
            ? "Multiple"
            : payments[0]?.type.charAt(0).toUpperCase() +
            payments[0]?.type.slice(1) || "Cash";

        // Calculate total tax (services + products)
        // Backend calculates tax from ItemTax table, but we send tax_percent for each item
        // We calculate it here to be consistent with the frontend display
        const calculatedTotalTax = tax;

        // Helper to ensure time is in H:i a format
        const formatToHiA = (timeStr: string): string => {
          if (!timeStr) return "";

          // Handle ISO string
          if (timeStr.includes("T") && timeStr.includes("Z")) {
            const date = new Date(timeStr);
            if (!isNaN(date.getTime())) {
              const hours = date.getHours();
              const minutes = date.getMinutes();
              const ampm = hours >= 12 ? "pm" : "am";
              const displayHours = hours % 12 || 12;
              return `${String(displayHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${ampm}`;
            }
          }

          // Handle range like "9.30–10.30 am"
          if (timeStr.includes("–")) {
            const startPart = timeStr.split("–")[0].trim();
            const timeParts = startPart.split(".");
            const hours = timeParts[0].padStart(2, "0");
            const minutes = (timeParts[1] || "00").padEnd(2, "0");
            const ampm = timeStr.toLowerCase().includes("pm") ? "pm" : "am";
            return `${hours}:${minutes} ${ampm}`;
          }

          return timeStr;
        };

        const finalFormattedTime = formatToHiA(finalPosTime);

        // Use full amounts (Gross) instead of reducing to Net.
        // The backend should record the full amount paid by the customer (e.g. 220)
        // and handle tax extraction based on the provided tax percentages or total_tax field.
        // const netToGrossRatio = total > 0 ? (subtotal / total) : 1;
        // Reverting this logic to ensure we store what was actually paid.
        const netAmountPaid = totalTendered;
        const netPendingAmount = toPay;
        const netChangeDue = changeDue;

        const cashOnly = isCashOnlyTender(tenderedPayments);

        // Check if we're paying for an existing booking
        let response: PosStoreResponse | undefined;
        let queuedOffline = false;

        if (paymentBookingId) {
          if (isBrowserOffline()) {
            throw new Error("Cannot complete booking payment while offline.");
          }

          try {
            const { addPaymentFromShow } = await import("../services/bookings");

            const paymentMode =
              payments.length > 0
                ? payments.map((p) => ({ type: p.type, amount: p.amount }))
                : [{ type: "cash" as const, amount: netAmountPaid }];

            const paymentResponse = await addPaymentFromShow(paymentBookingId, {
              amountPaid: netAmountPaid,
              total: actualTotalToPay + (initialAmountPaid || 0),
              paymentMode,
              changeDue: netChangeDue,
            });

            response = {
              payment_status:
                (paymentResponse?.payment_status as "completed" | "pending") ||
                (netPendingAmount <= 0 ? "completed" : "pending"),
              booking: {
                id: paymentBookingId,
                ...paymentResponse,
              },
              message:
                paymentResponse?.message || "Payment added successfully",
            };
          } catch (apiError) {
            console.error("Failed to add booking payment:", apiError);
            throw apiError;
          }
        } else {
          // Prepare payload matching backend format
          const dealIds: (string | number)[] = [];
          const dealQuantities: number[] = [];
          const dealPrices: number[] = [];
          if (deals && deals.length > 0) {
            deals.forEach((deal) => {
              if (deal.id) {
                dealIds.push(deal.id);
                dealQuantities.push(deal.quantity || 1);
                dealPrices.push(deal.price || 0);
              }
            });
          }

          const payload = {
            // Services (if provided)
            cart_services: serviceIds.length > 0 ? serviceIds : undefined,
            cart_quantity:
              serviceQuantities.length > 0 ? serviceQuantities : undefined,
            cart_prices: servicePrices.length > 0 ? servicePrices : undefined,
            tax_percent:
              serviceTaxPercents.length > 0 ? serviceTaxPercents : undefined,
            // Products (from POS)
            product_cart_services:
              productIds.length > 0 ? productIds : undefined,
            product_cart_quantity:
              productQuantities.length > 0 ? productQuantities : undefined,
            product_cart_prices:
              productPrices.length > 0 ? productPrices : undefined,
            product_tax_percent:
              productTaxPercents.length > 0 ? productTaxPercents : undefined,
            // Deals
            cart_deals: dealIds.length > 0 ? dealIds : undefined,
            deal_cart_quantity:
              dealQuantities.length > 0 ? dealQuantities : undefined,
            deal_cart_prices: dealPrices.length > 0 ? dealPrices : undefined,
            // Sunbeds (if provided)
            calendar_sunbeds:
              sunbeds && sunbeds.length > 0 ? sunbeds : undefined,
            calendar_memberships:
              memberships && memberships.length > 0 ? memberships : undefined,
            location: locationId,
            pos_date: finalPosDate, // Y-m-d format
            pos_time: finalFormattedTime, // H:i a format
            date: dateDMY, // d-m-Y format (alternative)
            time: finalFormattedTime, // H:i a format (alternative)
            posTime: finalFormattedTime, // H:i a format (alternative)
            user_id: userId,
            payments:
              payments.length > 0
                ? payments.map((p) => ({ ...p, amount: p.amount }))
                : [{ type: "cash" as const, amount: 0 }],
            payment_gateway: paymentGateway,
            pending_amount: netPendingAmount,
            amount_paid: netAmountPaid,
            change_due: netChangeDue.toFixed(2),
            type: type,
            serviceType: "offline" as const,
            total_tax: calculatedTotalTax, // Send the calculated tax explicitly
            cart_discount: cartDiscountPercent,
            coupon_id: appliedCoupon?.id ?? "",
            coupon_amount: appliedCoupon?.amount ?? "",
            coupon: appliedCoupon?.code ?? "",
            uuid,
            transaction_id: transactionId,
            employee: employees || undefined,
            consultation_form_ids: consultation_form_ids,
            // Backend expects in_store_usage[] only for product items (same order as product_cart_services)
            in_store_usage:
              productIds.length > 0
                ? Array(productIds.length).fill(isStoreUsage ? 1 : 0)
                : undefined,
          };

          const queueCurrentSale = () => {
            const queued = enqueueOfflinePosSale(
              createQueuedPosSaleInput(
                payload,
                netAmountPaid,
                Number(netChangeDue.toFixed(2)),
              ),
            );
            queuedOffline = true;
            setIsOfflineQueuedSale(true);
            response = {
              payment_status: "pending" as const,
              message:
                "Sale saved offline. It will sync when you are back online.",
              booking: {
                id: Date.now(),
                payment_status: "pending",
                amount_to_pay: totalAfterCoupon,
                formated_amount_to_pay: `${currencySymbol}${totalAfterCoupon.toFixed(2)}`,
              } as PosStoreResponse["booking"],
              booking_id: queued.id,
            };
          };

          if (isBrowserOffline()) {
            if (!cashOnly) {
              throw new Error("Only cash payments can be saved offline.");
            }
            queueCurrentSale();
          } else {
            try {
              response = await storePosBooking(payload);
            } catch (apiError) {
              if (cashOnly && shouldUseCachedData(apiError)) {
                console.warn("POS API unavailable — queueing cash sale offline");
                queueCurrentSale();
              } else {
                console.error("API call failed:", apiError);
                throw apiError;
              }
            }
          }
        }

        // Calculate elapsed time
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minProcessingTime - elapsedTime);

        // If we get here, API call was successful - show success modal
        const bookingId = response?.booking?.id ?? response?.booking_id ?? null;

        // Phone / pay-later deposit: reuse same Xonder online deposit payment link
        if (
          !queuedOffline &&
          !paymentBookingId &&
          type === "pay_later" &&
          bookingId
        ) {
          const depositDue = Number(
            (response?.booking as any)?.deposit ?? 0
          );
          if (depositDue >= 0.5) {
            try {
              const { createDepositPaymentLink } = await import(
                "../services/bookings"
              );
              const linkRes = await createDepositPaymentLink(bookingId, true);
              toast.success(
                linkRes?.message ||
                  "Deposit payment link sent to customer."
              );
            } catch (linkErr: any) {
              toast.error(
                linkErr?.message ||
                  "Sale saved, but deposit link could not be sent."
              );
            }
          }
        }

        // Store API response and booking ID
        setApiResponse(response);
        setCompletedBookingId(bookingId);

        // Wait for minimum processing time before showing success
        setTimeout(() => {
          // Dispatch complete to customer display immediately before any state gap
          dispatchDisplayUpdate({
            status: "complete",
            totalTendered: netAmountPaid,
            changeDue: netChangeDue,
            amountPaid: response?.booking?.amount_to_pay ?? netAmountPaid,
          });

          // Success - hide processing modal first
          setIsSubmitting(false);
          setIsPaid(true);

          // Delay to allow processing modal to fade out before showing success modal
          setTimeout(() => {
            // Show success modal with payment summary
            setShowSuccessModal(true);
            toast.success(
              queuedOffline
                ? "Cash sale saved offline — it will sync when back online."
                : "Payment processed successfully",
            );
            // Don't call onPaymentComplete here - wait for user to click Done button
          }, 500);
        }, remainingTime);
      } catch (error: any) {
        console.error("Error processing payment:", error);
        // Calculate elapsed time for error case
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minProcessingTime - elapsedTime);

        // Wait for minimum processing time before showing error
        setTimeout(() => {
          setIsSubmitting(false);
          // Reset flags on error so user can retry
          apiCallAttemptedRef.current = false;
          paymentProcessedRef.current = false;
          setIsPaid(false);
          setHasError(true);
          toast.error(
            error.response?.data?.message ||
            "Failed to process payment. Please try again.",
          );
        }, remainingTime);
      }
    },
    [
      locationId,
      userId,
      products,
      services,
      employees,
      tenderedPayments,
      total,
      tax,
      totalTendered,
      toPay,
      changeDue,
      appliedCoupon,
      cartDiscountPercent,
      paymentBookingId,
      initialAmountPaid,
      initialPendingAmount,
      onPaymentComplete,
      dispatchDisplayUpdate,
      onClose,
      isSubmitting,
    ],
  );

  // Mirror tender progress to the customer display
  // Skip when a higher-priority phase (card, saving, complete) is active
  React.useEffect(() => {
    if (!onCustomerDisplayUpdate) return;
    if (isSubmitting || isPaid || showSuccessModal) return;

    const phase = displayPhaseRef.current;
    if (phase === "card" || phase === "saving" || phase === "complete") return;

    if (tenderedPayments.length === 0) {
      dispatchDisplayUpdate({
        status: "awaiting",
        tenderLabel: null,
        amountDue: toPay,
        totalTendered: 0,
        changeDue: 0,
      });
      return;
    }

    const lastTender = tenderedPayments[tenderedPayments.length - 1];
    dispatchDisplayUpdate({
      status: "tendered",
      tenderLabel: getPaymentMethodLabel(lastTender.type),
      amountDue: toPay,
      totalTendered,
      changeDue,
    });
  }, [
    tenderedPayments,
    toPay,
    totalTendered,
    changeDue,
    isSubmitting,
    isPaid,
    showSuccessModal,
    onCustomerDisplayUpdate,
    dispatchDisplayUpdate,
  ]);

  // Auto-process payment when fully paid (toPay becomes 0)
  React.useEffect(() => {
    // Only call API if payment is complete AND API hasn't been attempted yet
    // Only call API if payment is complete AND API hasn't been attempted yet AND no error
    if (
      toPay === 0 &&
      tenderedPayments.length > 0 &&
      !isSubmitting &&
      !paymentProcessedRef.current &&
      !apiCallAttemptedRef.current &&
      !hasError
    ) {
      // Payment is complete, automatically process
      handlePaymentComplete("part_paid").catch((error) => {
        console.error("Payment processing error:", error);
        // Don't reset flags - API call was attempted once, prevent retry
      });
    }
  }, [
    toPay,
    tenderedPayments.length,
    isSubmitting,
    handlePaymentComplete,
    hasError,
  ]);

  // Reset error state when payments change so auto-submit can try again
  React.useEffect(() => {
    setHasError(false);
  }, [tenderedPayments.length]);

  return (
    <>
      <div
        className={
          isEmbedded
            ? "h-full w-full bg-[#F9F9F9] flex flex-col pay:flex-row relative"
            : "fixed top-0 right-0 h-full w-full pay:w-[60%] bg-[#F9F9F9] shadow-xl border-l border-gray-200 transform transition-transform duration-300 z-[1000] flex flex-col pay:flex-row"
        }
      >
        {/* Close Button - Only show if not embedded (parent handles it otherwise) */}
        {!isEmbedded && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={onClose}
            className="absolute pay:-left-14 top-4 pay:top-8 right-4 pay:right-auto w-10 h-10 flex items-center justify-center rounded-full bg-white text-[#885ABB] shadow-lg pay:shadow-[-4px_0_12px_rgba(0,0,0,0.15)] hover:bg-[#402B69] hover:text-white transition-all z-[60] group"
            aria-label="Close"
          >
            <X
              size={20}
              strokeWidth={3}
              className="group-hover:rotate-90 transition-transform duration-300"
            />
          </motion.button>
        )}

        {/* Left Side: Billing Methods - from 1019px up; below that show only keypad/summary */}
        <div className="hidden pay:block w-full pay:w-[30%] p-6 sm:p-8 relative bg-white border-b pay:border-b-0 pay:border-r border-gray-100 flex-shrink-0 overflow-y-auto">
          {isEmbedded && onBack && (
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-gray-500 hover:text-[#885ABB] transition-colors mb-6 font-semibold text-base"
            >
              <ChevronLeft size={16} />
              <span>Back</span>
            </button>
          )}

          <div className="mb-6 sm:mb-10">
            <h3 className="text-[18px] sm:text-[22px] font-bold text-[#402B69] tracking-tight">
              Billing Methods
            </h3>
            <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mt-1">
              Select Gateway
            </p>
          </div>

          <div className="grid grid-cols-3 pay:flex pay:flex-col gap-3">
            {[
              { id: "cash", label: "Cash", icon: Banknote },
              { id: "cheque", label: "Cheque", icon: FileText },
              { id: "card", label: "Card", icon: CreditCard },
            ].map((method) => {
              const Icon = method.icon;
              const isSelected = selectedPaymentMethod === method.id;
              const isDisabledOffline = isMethodDisabledWhileOffline(method.id);
              return (
                <button
                  key={method.id}
                  onClick={() => handlePaymentMethodClick(method.id as any)}
                  disabled={isDisabledOffline}
                  title={
                    isDisabledOffline && method.id === "card" && isPayWithXEConnect
                      ? "XE Connect card payments are unavailable offline"
                      : undefined
                  }
                  className={cn(
                    "w-full py-3 sm:py-3.5 px-3 sm:px-4 rounded-xl font-bold transition-all duration-300 flex flex-col pay:flex-row items-center pay:gap-3 text-sm sm:text-base",
                    isDisabledOffline
                      ? "cursor-not-allowed opacity-45 bg-gray-50 text-gray-400 border border-gray-100"
                      : isSelected
                      ? "bg-[#885ABB]/5 text-[#885ABB] border-2 border-[#885ABB] shadow-sm scale-[1.02]"
                      : "bg-white text-gray-500 border border-gray-100 hover:border-[#885ABB]/30 hover:bg-gray-50",
                  )}
                >
                  <div
                    className={cn(
                      "p-1.5 sm:p-2 rounded-lg transition-colors mb-1 pay:mb-0",
                      isSelected
                        ? "bg-[#885ABB]/10 text-[#885ABB]"
                        : "bg-gray-50 text-gray-400",
                    )}
                  >
                    <Icon size={22} />
                  </div>
                  <span>{getPaymentMethodLabelOffline(method.id, method.label)}</span>
                </button>
              );
            })}
            {showStoreUsageButton && (
              <button
                type="button"
                onClick={() => {
                  setSelectedPaymentMethod("store_usage");
                  setIsStoreUsage(true);
                  handlePaymentMethodClick("cash", {
                    skipSelect: true,
                    amount: toPay,
                  });
                }}
                className={cn(
                  "w-full py-3 sm:py-3.5 px-3 sm:px-4 rounded-xl font-bold transition-all duration-300 flex flex-col pay:flex-row items-center pay:gap-3 text-sm sm:text-base",
                  selectedPaymentMethod === "store_usage"
                    ? "bg-[#885ABB]/5 text-[#885ABB] border-2 border-[#885ABB] shadow-sm scale-[1.02]"
                    : "bg-white text-gray-500 border border-gray-100 hover:border-[#885ABB]/30 hover:bg-gray-50",
                )}
              >
                <div
                  className={cn(
                    "p-1.5 sm:p-2 rounded-lg transition-colors mb-1 pay:mb-0",
                    selectedPaymentMethod === "store_usage" ? "bg-[#885ABB]/10 text-[#885ABB]" : "bg-gray-50 text-gray-400",
                  )}
                >
                  <Store size={22} />
                </div>
                <span>Store Usage</span>
              </button>
            )}
            {isTabletApp &&
              (() => {
                const method = {
                  id: "card_reader",
                  label: "Card Reader",
                  icon: CreditCard,
                };
                const Icon = method.icon;
                const isSelected = selectedPaymentMethod === method.id;
                const isDisabledOffline = isMethodDisabledWhileOffline(method.id);
                return (
                  <button
                    key={method.id}
                    onClick={() => handlePaymentMethodClick(method.id as any)}
                    disabled={isDisabledOffline}
                    className={cn(
                      "w-full py-3 sm:py-3.5 px-3 sm:px-4 rounded-xl font-bold transition-all duration-300 flex flex-col pay:flex-row items-center pay:gap-3 text-sm sm:text-base",
                      isDisabledOffline
                        ? "cursor-not-allowed opacity-45 bg-gray-50 text-gray-400 border border-gray-100"
                        : isSelected
                        ? "bg-[#885ABB]/5 text-[#885ABB] border-2 border-[#885ABB] shadow-sm scale-[1.02]"
                        : "bg-white text-gray-500 border border-gray-100 hover:border-[#885ABB]/30 hover:bg-gray-50",
                    )}
                  >
                    <div
                      className={cn(
                        "p-1.5 sm:p-2 rounded-lg transition-colors mb-1 pay:mb-0",
                        isSelected
                          ? "bg-[#885ABB]/10 text-[#885ABB]"
                          : "bg-gray-50 text-gray-400",
                      )}
                    >
                      <Icon size={22} />
                    </div>
                    <span>{getPaymentMethodLabelOffline(method.id, method.label)}</span>
                  </button>
                );
              })()}
          </div>

          {/* Divider - when Billing Methods panel visible (1019px+) */}
          <div className="hidden pay:block absolute top-0 right-0 h-full w-[2px] bg-gray-200 shadow-[4px_0_12px_rgba(0,0,0,0.15)]"></div>
        </div>

        {/* Right Side: Calculator and Summary */}
        <div className="w-full pay:w-[70%] flex flex-col flex-1 bg-[#F9F9F9] overflow-hidden">
          {/* Compact payment method bar - only below 1019px (when Billing Methods panel is hidden) */}
          <div className="pay:hidden flex flex-wrap gap-2 p-3 bg-white border-b border-gray-100 shrink-0">
            {[
              { id: "cash", label: "Cash", icon: Banknote },
              { id: "cheque", label: "Cheque", icon: FileText },
              { id: "card", label: "Card", icon: CreditCard },
            ].map((method) => {
              const Icon = method.icon;
              const isSelected = selectedPaymentMethod === method.id;
              const isDisabledOffline = isMethodDisabledWhileOffline(method.id);
              return (
                <button
                  key={method.id}
                  onClick={() => handlePaymentMethodClick(method.id as any)}
                  disabled={isDisabledOffline}
                  title={
                    isDisabledOffline && method.id === "card" && isPayWithXEConnect
                      ? "XE Connect card payments are unavailable offline"
                      : undefined
                  }
                  className={cn(
                    "flex items-center gap-2 py-2.5 px-3.5 rounded-lg text-sm font-bold transition-all border",
                    isDisabledOffline
                      ? "cursor-not-allowed opacity-45 bg-gray-50 text-gray-400 border-gray-100"
                      : isSelected
                        ? "bg-[#885ABB]/10 text-[#885ABB] border-[#885ABB]"
                        : "bg-gray-50 text-gray-600 border-gray-100 hover:border-[#885ABB]/30",
                  )}
                >
                  <Icon size={18} />
                  <span>{getPaymentMethodLabelOffline(method.id, method.label)}</span>
                </button>
              );
            })}
            {showStoreUsageButton && (
              <button
                type="button"
                onClick={() => {
                  setSelectedPaymentMethod("store_usage");
                  setIsStoreUsage(true);
                  handlePaymentMethodClick("cash", { skipSelect: true, amount: toPay });
                }}
                className={cn(
                    "flex items-center gap-2 py-2.5 px-3.5 rounded-lg text-sm font-bold transition-all border",
                  selectedPaymentMethod === "store_usage" ? "bg-[#885ABB]/10 text-[#885ABB] border-[#885ABB]" : "bg-gray-50 text-gray-600 border-gray-100 hover:border-[#885ABB]/30",
                )}
              >
                <Store size={18} />
                <span>Store Usage</span>
              </button>
            )}
          </div>
          {/* Keypad — half screen height */}
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden p-4 sm:p-5 pay:py-4">
            <div className="mb-4 pay:mb-5 shrink-0">
              <div className="text-4xl sm:text-5xl lgg:text-[56px] font-bold text-[#402B69] text-center">
                {currencySymbol} {paymentAmount}
              </div>
            </div>

            <div className="flex-1 min-h-0 flex justify-center items-stretch mb-2 pay:mb-3 w-full pt-1 pay:pt-2">
              <div className="flex flex-col pay:flex-row gap-2 pay:gap-3 h-full w-full max-w-4xl mx-auto px-2">
                <div className="grid grid-cols-3 grid-rows-4 gap-2 flex-[3] min-h-0 h-full w-full min-w-0">
                  {["1", "2", "3", "4", "5", "6", "7", "8", "9", "00", "0", "."].map((k) => (
                    <button
                      key={k}
                      onClick={() => handleKey(k)}
                      className="w-full h-full min-h-0 bg-white border rounded-lg pay:rounded-xl text-lg pay:text-xl pos13:text-[25px] font-bold border-[#885ABB] text-[#885ABB] transition-all active:scale-95 shadow-sm"
                    >
                      {k}
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-2 grid-rows-4 gap-2 flex-[2] min-h-0 h-full w-full min-w-0">
                  {[1, 5, 10, 20, 30, 50].map((val) => (
                    <button
                      key={val}
                      onClick={() => handleDenomination(val)}
                      className="w-full h-full min-h-0 bg-[#885ABB]/5 border border-[#885ABB]/10 text-[#885ABB] rounded-lg pay:rounded-xl text-sm pay:text-base pos13:text-[25px] font-black hover:bg-[#885ABB] hover:text-white transition-all shadow-sm"
                    >
                      {currencySymbol}{val}
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      setIsUserTyping(false);
                      setPaymentAmount(toPay.toFixed(2));
                      setHasDecimal(false);
                      setDecimalDigits(0);
                    }}
                    className="w-full h-full min-h-0 bg-white border-2 border-[#885ABB]/20 rounded-lg pay:rounded-xl flex items-center justify-center hover:bg-[#885ABB] hover:text-white transition-all shadow-sm group"
                    title="Reset to To Pay"
                  >
                    <Target className="w-5 h-5 lgg:w-6 lgg:h-6 group-hover:scale-110 transition-transform" />
                  </button>
                  <button
                    onClick={() => handleKey("del")}
                    className="w-full h-full min-h-0 bg-red-50 border-2 border-red-50 text-red-500 rounded-lg pay:rounded-xl flex items-center justify-center hover:bg-red-500 hover:text-white transition-all active:scale-95 shadow-sm group"
                  >
                    <X className="w-5 h-5 lgg:w-6 lgg:h-6 stroke-[3]" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Payment summary — half screen height */}
          <div className="flex flex-col flex-1 min-h-0 bg-white border-t border-gray-100 shadow-[0_-10px_30px_rgba(0,0,0,0.03)]">
            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar px-4 sm:px-6 pt-4 pb-3">
              {/* Before tender */}
              {tenderedPayments.length === 0 && (
                <div className="space-y-3 pos13:space-y-3.5">
                  <div className="flex justify-between items-center text-base pos13:text-[19px] leading-none text-gray-500 font-medium">
                    <span>Subtotal</span>
                    <span>{currencySymbol}{subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center text-base pos13:text-[19px] leading-none text-gray-400 font-medium">
                    <span>Taxes</span>
                    <span>{currencySymbol}{tax.toFixed(2)}</span>
                  </div>

                  {cartDiscount > 0 && (
                    <div className="flex justify-between items-center text-base pos13:text-[19px] leading-none text-amber-600 font-medium">
                      <span>
                        Discount
                        {cartDiscountType === "percent"
                          ? ` (${cartDiscountPercent.toFixed(1)}%)`
                          : ""}
                      </span>
                      <span>
                        −{currencySymbol}
                        {cartDiscount.toFixed(2)}
                      </span>
                    </div>
                  )}

                  {couponDiscount > 0 && (
                    <div className="flex justify-between items-center text-base pos13:text-[19px] leading-none text-emerald-600 font-medium">
                      <span>Coupon ({appliedCoupon?.code})</span>
                      <span>
                        −{currencySymbol}
                        {couponDiscount.toFixed(2)}
                      </span>
                    </div>
                  )}

                  <div className="pt-3 border-t border-gray-100 flex justify-between items-center">
                    <span className="text-lg pos13:text-[22px] leading-none font-semibold text-[#402B69]">Total</span>
                    <span className="text-2xl pos13:text-[22px] leading-none font-bold text-[#885ABB]">
                      {currencySymbol}{totalAfterCoupon.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              {/* After tender */}
              {tenderedPayments.length > 0 && (
                <div className="space-y-3 pos13:space-y-3.5">
                  {(couponDiscount > 0 || cartDiscount > 0) ? (
                    <>
                      <div className="flex justify-between items-center text-base pos13:text-[22px] leading-none text-gray-500 font-medium">
                        <span>Total</span>
                        <span className="font-semibold text-[#402B69]">
                          {currencySymbol}
                          {total.toFixed(2)}
                        </span>
                      </div>
                      {cartDiscount > 0 && (
                        <div className="flex justify-between items-center text-sm text-amber-600 font-medium">
                          <span>Discount</span>
                          <span>
                            −{currencySymbol}
                            {cartDiscount.toFixed(2)}
                          </span>
                        </div>
                      )}
                      {couponDiscount > 0 && (
                        <div className="flex justify-between items-center text-sm text-emerald-600 font-medium">
                          <span>Coupon ({appliedCoupon?.code})</span>
                          <span>
                            −{currencySymbol}
                            {couponDiscount.toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between items-center text-base pos13:text-[22px] leading-none font-semibold text-[#402B69]">
                        <span>Payable</span>
                        <span>
                          {currencySymbol}
                          {actualTotalToPay.toFixed(2)}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between items-center text-base pos13:text-[22px] leading-none text-gray-500 font-medium">
                      <span>Total</span>
                      <span className="font-semibold text-[#402B69]">
                        {currencySymbol}
                        {actualTotalToPay.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="pt-3 border-t border-gray-100 space-y-2.5 pos13:space-y-3">
                    <div className="text-xs pos13:text-[22px] leading-none font-bold text-gray-400 uppercase tracking-widest">
                      Tendered
                    </div>
                    {tenderedPayments.map((payment) => (
                      <div
                        key={payment.id}
                        className="flex justify-between items-center text-base pos13:text-[22px] leading-none py-0.5"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-gray-600">{getPaymentMethodLabel(payment.type)}</span>
                          <button
                            onClick={() => handleRemovePayment(payment.id)}
                            className="p-0.5 rounded-md text-[#885ABB] hover:text-white hover:bg-[#885ABB] transition-all duration-200"
                            title="Remove payment"
                          >
                            <Trash2 size={14} className="pos13:w-[18px] pos13:h-[18px]" />
                          </button>
                        </div>
                        <span className="font-semibold text-[#402B69]">
                          {currencySymbol}{payment.amount.toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* To Pay — always bottom-stuck */}
            <div className="shrink-0 px-4 sm:px-6 py-3.5 pos13:py-4 bg-white border-t border-gray-100 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
              <div className="flex justify-between items-center gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-lg pos13:text-[22px] leading-none font-bold text-[#402B69]">
                    To Pay
                  </span>
                  {!paymentBookingId && (
                    <>
                      <button
                        type="button"
                        onClick={openDiscountModal}
                        disabled={isSubmitting || isPaid || showSuccessModal || !canApplyCartDiscount}
                        className={cn(
                          "relative shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-lg border transition-all",
                          cartDiscount > 0
                            ? "border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
                            : "border-[#885ABB]/25 bg-[#885ABB]/5 text-[#885ABB] hover:bg-[#885ABB] hover:text-white",
                          "disabled:opacity-50 disabled:cursor-not-allowed",
                        )}
                        title={cartDiscount > 0 ? "Edit discount" : "Apply discount"}
                        aria-label={cartDiscount > 0 ? "Edit discount" : "Apply discount"}
                      >
                        <Percent size={18} />
                        {cartDiscount > 0 ? (
                          <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-white" />
                        ) : null}
                      </button>
                      <button
                        type="button"
                        onClick={openCouponModal}
                        disabled={isSubmitting || isPaid || showSuccessModal}
                        className={cn(
                          "relative shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-lg border transition-all",
                          appliedCoupon
                            ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                            : "border-[#885ABB]/25 bg-[#885ABB]/5 text-[#885ABB] hover:bg-[#885ABB] hover:text-white",
                          "disabled:opacity-50 disabled:cursor-not-allowed",
                        )}
                        title={appliedCoupon ? `Coupon ${appliedCoupon.code}` : "Apply coupon"}
                        aria-label={appliedCoupon ? "Edit coupon" : "Apply coupon"}
                      >
                        <Tag size={18} />
                        {appliedCoupon ? (
                          <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white" />
                        ) : null}
                      </button>
                    </>
                  )}
                </div>
                <span className="text-2xl pos13:text-[22px] leading-none font-black text-[#885ABB]">
                  {currencySymbol}
                  {showSuccessModal || isPaid
                    ? "0.00"
                    : toPay === 0
                      ? actualTotalToPay.toFixed(2)
                      : toPay.toFixed(2)}
                </span>
              </div>
              {cartDiscount > 0 && !paymentBookingId ? (
                <div className="mt-2 flex items-center justify-between gap-2 text-xs text-amber-700">
                  <span className="truncate font-semibold">
                    Discount (−{currencySymbol}
                    {cartDiscount.toFixed(2)})
                  </span>
                  <button
                    type="button"
                    onClick={handleRemoveCartDiscount}
                    className="shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-md text-amber-700 hover:bg-amber-100"
                    title="Remove discount"
                    aria-label="Remove discount"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ) : null}
              {appliedCoupon && !paymentBookingId ? (
                <div className="mt-2 flex items-center justify-between gap-2 text-xs text-emerald-700">
                  <span className="truncate font-semibold">
                    {appliedCoupon.code} (−{currencySymbol}
                    {appliedCoupon.amount.toFixed(2)})
                  </span>
                  <button
                    type="button"
                    onClick={handleRemoveCoupon}
                    className="shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-md text-emerald-700 hover:bg-emerald-100"
                    title="Remove coupon"
                    aria-label="Remove coupon"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ) : null}
            </div>

            {tenderedPayments.length > 0 && (
              <div className="shrink-0 px-4 sm:px-6 pt-3 pb-4 bg-white border-t border-gray-100">
                <button
                  onClick={() => handlePaymentComplete("part_paid")}
                  disabled={isSubmitting}
                  className="w-full h-12 sm:h-14 bg-[#885ABB] text-white rounded-xl font-bold hover:bg-[#7246a0] transition-all text-sm sm:text-base uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg shadow-[#885ABB]/20"
                >
                  {isSubmitting ? (
                    <Loader2 className="animate-spin" size={20} />
                  ) : (
                    <>
                      <CheckCircle size={20} />
                      <span>Post Transaction</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Processing Modal - Rendered outside drawer */}
      {isSubmitting && (
        // <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] animate-fadeIn">
        //   <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-[90%] mx-4 transform transition-all duration-300 animate-scaleIn">
        //     {/* Spinner Icon with Animation */}
        //     <div className="flex justify-center mb-6">
        //       <div className="relative">
        //         <Loader2 className="w-16 h-16 text-[#885ABB] animate-spin" />
        //       </div>
        //     </div>

        //     {/* Processing Message */}
        //     <div className="text-center mb-6">
        //       <h2 className="text-3xl font-bold text-[#402B69] mb-3 animate-slideUp">
        //         Processing Payment...
        //       </h2>
        //       <p className="text-lg text-gray-600 animate-slideUp delay-100">
        //         Please wait while we process your payment
        //       </p>
        //     </div>

        //     {/* Animated Dots */}
        //     <div className="flex justify-center gap-2">
        //       <div className="w-4 h-4 bg-[#885ABB] rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
        //       <div className="w-4 h-4 bg-[#885ABB] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
        //       <div className="w-4 h-4 bg-[#885ABB] rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
        //     </div>
        //   </div>
        // </div>
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1100] animate-fadeIn backdrop-blur-[2px]">
          <GlobalLoader
            initialPageLoad={false}
            description="Processing Payment..."
            compensateSidebar={false}
          />
        </div>
      )}

      {/* Coupon apply modal */}
      {couponModalOpen && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b bg-gray-50">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[#885ABB]/10 text-[#885ABB]">
                  <Tag size={18} />
                </span>
                <h3 className="text-lg font-semibold text-[#402B69]">
                  {appliedCoupon ? "Coupon applied" : "Apply coupon"}
                </h3>
              </div>
              <button
                type="button"
                onClick={closeCouponModal}
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-200"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {appliedCoupon ? (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-emerald-800 truncate">
                        {appliedCoupon.code}
                      </p>
                      <p className="text-sm text-emerald-700 mt-1">
                        Discount −{currencySymbol}
                        {appliedCoupon.amount.toFixed(2)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveCoupon}
                      className="shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white border border-emerald-200 text-red-500 hover:bg-red-50"
                      title="Remove coupon"
                      aria-label="Remove coupon"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-[#402B69] mb-1.5">
                      Coupon code
                    </label>
                    <input
                      type="text"
                      autoFocus
                      value={couponCode}
                      onChange={(e) => {
                        setCouponCode(e.target.value.toUpperCase());
                        setCouponError(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          void handleApplyCoupon();
                        }
                      }}
                      placeholder="Enter code"
                      disabled={!canApplyCoupon || couponLoading}
                      className="w-full h-11 px-3 rounded-xl border border-gray-200 text-sm font-semibold uppercase tracking-wide focus:outline-none focus:ring-2 focus:ring-[#885ABB]/30 focus:border-[#885ABB]"
                    />
                  </div>
                  {couponError ? (
                    <p className="text-sm text-red-500">{couponError}</p>
                  ) : null}
                  {!canApplyCoupon && isOffline ? (
                    <p className="text-sm text-amber-600">
                      Coupons require an internet connection.
                    </p>
                  ) : null}
                </>
              )}
            </div>

            <div className="px-5 py-4 border-t bg-white flex gap-2">
              {appliedCoupon ? (
                <button
                  type="button"
                  onClick={closeCouponModal}
                  className="flex-1 h-11 rounded-xl bg-[#885ABB] text-white text-sm font-semibold hover:bg-[#7246a0]"
                >
                  Done
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={closeCouponModal}
                    disabled={couponLoading}
                    className="flex-1 h-11 rounded-xl bg-gray-100 text-gray-800 text-sm font-semibold hover:bg-gray-200 disabled:opacity-60"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleApplyCoupon()}
                    disabled={
                      !canApplyCoupon || couponLoading || !couponCode.trim()
                    }
                    className="flex-1 h-11 rounded-xl bg-[#885ABB] text-white text-sm font-semibold hover:bg-[#7246a0] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                  >
                    {couponLoading ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      "Apply"
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cart discount modal */}
      {discountModalOpen && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b bg-gray-50">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-700">
                  <Percent size={18} />
                </span>
                <h3 className="text-lg font-semibold text-[#402B69]">
                  {cartDiscount > 0 ? "Edit discount" : "Apply discount"}
                </h3>
              </div>
              <button
                type="button"
                onClick={closeDiscountModal}
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-200"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="flex gap-2 p-1 bg-gray-100 rounded-xl">
                <button
                  type="button"
                  onClick={() => setCartDiscountType("percent")}
                  className={cn(
                    "flex-1 h-10 rounded-lg text-sm font-semibold transition-all",
                    cartDiscountType === "percent"
                      ? "bg-white text-[#885ABB] shadow-sm"
                      : "text-gray-500",
                  )}
                >
                  Percent
                </button>
                <button
                  type="button"
                  onClick={() => setCartDiscountType("fixed")}
                  className={cn(
                    "flex-1 h-10 rounded-lg text-sm font-semibold transition-all",
                    cartDiscountType === "fixed"
                      ? "bg-white text-[#885ABB] shadow-sm"
                      : "text-gray-500",
                  )}
                >
                  Fixed
                </button>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#402B69] mb-1.5">
                  {cartDiscountType === "percent"
                    ? "Discount %"
                    : `Discount amount (${currencySymbol})`}
                </label>
                <input
                  type="number"
                  min="0"
                  step={cartDiscountType === "percent" ? "0.1" : "0.01"}
                  autoFocus
                  value={cartDiscountInput}
                  onChange={(e) => setCartDiscountInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleApplyCartDiscount();
                    }
                  }}
                  placeholder={cartDiscountType === "percent" ? "e.g. 10" : "e.g. 5.00"}
                  className="w-full h-11 px-3 rounded-xl border border-gray-200 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#885ABB]/30 focus:border-[#885ABB]"
                />
              </div>

              {cartDiscount > 0 ? (
                <button
                  type="button"
                  onClick={handleRemoveCartDiscount}
                  className="text-sm font-semibold text-red-500 hover:underline"
                >
                  Remove discount
                </button>
              ) : null}
            </div>

            <div className="px-5 py-4 border-t bg-white flex gap-2">
              <button
                type="button"
                onClick={closeDiscountModal}
                className="flex-1 h-11 rounded-xl bg-gray-100 text-gray-800 text-sm font-semibold hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApplyCartDiscount}
                disabled={!canApplyCartDiscount || !cartDiscountInput.trim()}
                className="flex-1 h-11 rounded-xl bg-[#885ABB] text-white text-sm font-semibold hover:bg-[#7246a0] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal - Rendered outside drawer */}
      <PaymentCompletedModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          onClose();
        }}
        apiResponse={apiResponse}
        totalTendered={totalTendered}
        changeDue={changeDue}
        bookingId={completedBookingId}
        onPaymentComplete={onPaymentComplete}
        isOfflineQueued={isOfflineQueuedSale}
      />
    </>
  );
};

export default PosPaymentStep;
